/** Pure scoring helpers for EXT.MATCH — mirrored in SQL dispatch_requirements_score. */

export interface RequirementsContext {
  requirementsText: string;
  vehicleCapacity: number;
  /** @deprecated Matching uses vehicleFeatures only; kept for call-site compat. */
  vehicleNotes?: string;
  vehicleModel?: string;
  /**
   * Capability tags (e.g. wheelchair, ac). Empty array = no capability features.
   * Matching uses features + capacity only — not notes/model.
   */
  vehicleFeatures?: string[];
}

export interface DispatchScoreBreakdown {
  distance_score: number;
  capacity_fit_score: number;
  utilization_score: number;
  requirements_score: number;
  anchor_type?: string;
}

export function scoreCapacityFit(capacity: number, passengers: number): number {
  if (capacity < passengers) return 0;
  if (capacity <= 0) return 0;
  return 100 * (1 - (capacity - passengers) / capacity);
}

export function scoreDistance(distanceMeters: number | null | undefined, unknownFallback = 30): number {
  if (distanceMeters == null || Number.isNaN(distanceMeters)) {
    return unknownFallback;
  }
  return Math.max(0, 100 - distanceMeters / 100);
}

export function scoreRequirements(ctx: RequirementsContext): number {
  const req = (ctx.requirementsText || '').toLowerCase().trim();
  if (!req) return 50;

  const features = (ctx.vehicleFeatures || []).map((f) => f.toLowerCase());
  let hasReq = false;
  let matched = true;

  if (/(wheelchair|轮椅|accessible)/.test(req)) {
    hasReq = true;
    if (!features.includes('wheelchair')) matched = false;
  }
  if (/(mini|小型|small bus)/.test(req)) {
    hasReq = true;
    if (ctx.vehicleCapacity > 24) matched = false;
  }
  if (/(coach|大巴|large bus)/.test(req)) {
    hasReq = true;
    if (ctx.vehicleCapacity < 40) matched = false;
  }
  // Word-boundary on "ac" so "coach" does not spuriously require the ac feature.
  if (/(^|[^a-z])ac([^a-z]|$)|空调|air.?cond/.test(req)) {
    hasReq = true;
    if (!features.includes('ac')) matched = false;
  }


  if (!hasReq) return 50;
  return matched ? 100 : 0;
}

export function computeTotalDispatchScore(breakdown: DispatchScoreBreakdown): number {
  return Math.round(
    (0.4 * breakdown.distance_score +
      0.25 * breakdown.capacity_fit_score +
      0.2 * breakdown.utilization_score +
      0.15 * breakdown.requirements_score) *
      10
  ) / 10;
}

export function formatDispatchBreakdown(breakdown: DispatchScoreBreakdown): string {
  return [
    `Distance: ${breakdown.distance_score.toFixed(0)}`,
    `Capacity fit: ${breakdown.capacity_fit_score.toFixed(0)}`,
    `Utilization: ${breakdown.utilization_score.toFixed(0)}`,
    `Requirements: ${breakdown.requirements_score.toFixed(0)}`,
    breakdown.anchor_type ? `Anchor: ${breakdown.anchor_type}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
